/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react';

import { useEffect } from 'react';
import Media from 'react-media';

import Uri from 'jsuri';
import { camelCase, startCase } from 'lodash';

import requestUrlInfo from 'js/lib/requestUrlInfo';
import { useRouter } from 'js/lib/useRouter';

import { Grid } from '@coursera/cds-core';

import DomainTopSkills from 'bundles/browse/components/Domain/DomainTopSkills';
import OtherTopics from 'bundles/browse/components/Domain/OtherTopics';
import { getSkillsForSubDomain } from 'bundles/browse/components/Domain/utils/subdomainUtils';
import BrowseFAQs from 'bundles/browse/components/Faq/BrowseFAQs';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import SearchBar from 'bundles/search-common/components/searchbar/SearchBar';
import { TABLET_SCREEN_WIDTH } from 'bundles/search-common/constants';
import { isSearchTheme } from 'bundles/search-common/searchTheme';
import { getNameFromDomainOrSubdomainId } from 'bundles/search-common/utils/SearchUtils';
import { getShouldShowBrowseInSERP, getShowNewAutocomplete } from 'bundles/search-common/utils/experimentUtils';
import SearchPageRoot from 'bundles/search-v2/components/SearchPageRoot';
import ContentBlock from 'bundles/seo-entity-page/components/ContentBlock';
import EntityPageCanonicalTag from 'bundles/seo-entity-page/components/EntityPageCanonicalTag';
import EntityPageHeader from 'bundles/seo-entity-page/components/EntityPageHeader';
import { SeoSearchProviderWrapper } from 'bundles/seo-entity-page/components/SeoSearchProviderWrapper';
import { getCurrentEntityPageNum } from 'bundles/seo-entity-page/components/utils';
import { QUERY_TO_SUBDOMAIN_SKILL_MAPPING } from 'bundles/seo-entity-page/constants';
import { useSeoPageData } from 'bundles/seo/contexts/SeoPageDataContext';

export const SEOEntityPage = () => {
  const router = useRouter();
  const theme = useTheme();
  const userAgent = useUserAgent();
  const { eqpData } = useSeoPageData();
  const shouldShowBrowseInSERP = getShouldShowBrowseInSERP();

  const domainSkillsHeader = eqpData?.headers?.domainSkillsHeader?.label;
  const searchQuery = router.location.query;
  const { search, pathname } = router.location;
  const hasSearchQuery = !!searchQuery.query;
  const showTopContentBlock = eqpData?.topContentBlock && !shouldShowBrowseInSERP;
  const showBottomContentBlock = eqpData?.bottomContentBlock;
  const topContentBlock = showTopContentBlock && eqpData.topContentBlock !== null ? eqpData.topContentBlock : undefined;
  const bottomContentBlock =
    showBottomContentBlock && eqpData.bottomContentBlock !== null ? eqpData.bottomContentBlock : undefined;
  const searchPageRootProps = { isSEOEntityPage: !!searchQuery.query, router };
  useEffect(() => {
    // if search query is not in entity list, redirect to /search on client or show 404

    let entityQuery: string;

    // Spaces are usually replaced with `%20` or `+` in encoded urls. However,
    // space characters within the query string of the url are usually encoded as `+`.
    // Therefore, any query values containing a `+` symbol as part of the value for example `C++` must
    // be encoded in order for the symbol to be interpreted as part of the value instead of a space.

    // We are replacing all `+` space representations with `%20` to match the canonical urls and prevent duplicate entity pages
    if (search && /\+/.test(search)) {
      entityQuery = search.replace(/\+/g, '%20');
      const redirectUrl = `${pathname}${entityQuery}`;
      router.replace(redirectUrl);
    }
  }, [router, searchQuery, search, pathname, hasSearchQuery]);

  const renderRelatedSkills = () => {
    const entityPageMappedSubdomain = QUERY_TO_SUBDOMAIN_SKILL_MAPPING[searchQuery.query];

    if (!entityPageMappedSubdomain) {
      return null;
    }

    const subdomainId = entityPageMappedSubdomain.subdomain;
    const domainId = entityPageMappedSubdomain.domain;

    const skillsList = getSkillsForSubDomain(subdomainId);

    if (!skillsList.length) {
      return null;
    }

    return (
      <DomainTopSkills
        headerLabel={domainSkillsHeader}
        domainName={getNameFromDomainOrSubdomainId(domainId)}
        subDomainName={getNameFromDomainOrSubdomainId(subdomainId)}
        skillsList={skillsList}
        isMobileBrowser={userAgent?.isMobileBrowser ?? false}
        isEntityPage
        entityPageUrl={pathname + search}
      />
    );
  };

  const renderFaqs = () => {
    const titleCaseSearchQuery = startCase(camelCase(searchQuery.query));
    const titleCaseEQPDataFaqTopic = eqpData?.faqSlug ? startCase(camelCase(eqpData?.faqSlug)) : titleCaseSearchQuery;
    const currentPage = getCurrentEntityPageNum(router);
    const isOnFirstPage = currentPage === 1;
    // We should only render FAQs if there exists a search query term and if you're on page 1
    return (
      isOnFirstPage &&
      hasSearchQuery && (
        <BrowseFAQs isEntityPage appSlug="search" faqTopic={titleCaseEQPDataFaqTopic} faqSlug={eqpData?.faqSlug} />
      )
    );
  };

  const renderCanonicalTags = () => {
    const { query } = router.location;
    const hostname = requestUrlInfo().hostname;
    const queryEntries = Object.keys(query);

    const uri = new Uri().setProtocol('https').setHost(hostname).setPath(pathname);

    if (query?.query) {
      uri.addQueryParam('query', query.query);
    }

    if (queryEntries.length <= 6) {
      return <EntityPageCanonicalTag canonicalUrl={uri.toString()} />;
    }

    return null;
  };

  if (!isSearchTheme(theme)) {
    throw new Error(
      'SEOEntityPage can only be used in the context of a SearchTheme. Wrap this component with a ThemeProvider.'
    );
  }
  const showNewAutocomplete = getShowNewAutocomplete();

  return (
    <TrackedDiv
      className="rc-SEOEntityPage"
      trackingName="seo_entity_page"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
      atMostOnce
      data={{ searchQuery }}
      css={[theme.searchPageContainer, theme.entityPageContainer, theme.seoEntityNested]}
    >
      {renderCanonicalTags()}
      {!showNewAutocomplete && (
        <Media query={{ maxWidth: TABLET_SCREEN_WIDTH }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) => {
            return (
              matches && (
                <div className="mobile-search-page-bar">
                  <SearchBar isSearchPage={true} isSEOEntityPage={true} />
                </div>
              )
            );
          }}
        </Media>
      )}
      <EntityPageHeader userAgent={userAgent} contentBlock={topContentBlock} />
      <SeoSearchProviderWrapper isSEOEntityPage={!!searchQuery}>
        <SearchPageRoot {...searchPageRootProps} />
      </SeoSearchProviderWrapper>
      <Grid container css={theme.entityPageBody} spacing={8}>
        {bottomContentBlock && (
          <Grid item xs={12}>
            <ContentBlock
              label={bottomContentBlock.label}
              content={bottomContentBlock.content}
              data-testid="bottom-content-block"
            />
          </Grid>
        )}
        <Grid item container className="internal-linking-wrapper" xs={12} spacing={8}>
          <Grid item xs={12} className="skills-to-learn-section">
            {renderRelatedSkills()}
          </Grid>
          <Grid item xs={12} className="faq-and-topics-container cds">
            {renderFaqs()}
            <OtherTopics />
          </Grid>
        </Grid>
      </Grid>
    </TrackedDiv>
  );
};

export default SEOEntityPage;
